// Date utilities for Australian Eastern Standard Time (AEST)

export const AEST_TIMEZONE = 'Australia/Sydney';

/**
 * Get current date in AEST timezone
 */
export const getCurrentAESTDate = (): Date => {
  return new Date(new Date().toLocaleString("en-US", { timeZone: AEST_TIMEZONE }));
};

/**
 * Format date to AEST string for date inputs (YYYY-MM-DD)
 */
export const formatDateForInput = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-CA', { 
    timeZone: AEST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(dateObj);
};

/**
 * Format datetime for datetime-local inputs (YYYY-MM-DDTHH:mm)
 */
export const formatDateTimeForInput = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Get AEST date components
  const aestDate = new Date(dateObj.toLocaleString("en-US", { timeZone: AEST_TIMEZONE }));
  
  const year = aestDate.getFullYear();
  const month = String(aestDate.getMonth() + 1).padStart(2, '0');
  const day = String(aestDate.getDate()).padStart(2, '0');
  const hours = String(aestDate.getHours()).padStart(2, '0');
  const minutes = String(aestDate.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Format date for display in Australian format
 */
export const formatDisplayDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: AEST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(dateObj);
};

/**
 * Format datetime for display in Australian format
 */
export const formatDisplayDateTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: AEST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(dateObj);
};

/**
 * Get today's date in AEST as YYYY-MM-DD string
 */
export const getTodayAEST = (): string => {
  return formatDateForInput(getCurrentAESTDate());
};

/**
 * Get current datetime in AEST as YYYY-MM-DDTHH:mm string
 */
export const getNowAEST = (): string => {
  return formatDateTimeForInput(getCurrentAESTDate());
};

/**
 * Convert UTC date to AEST for display
 */
export const convertToAEST = (utcDate: Date | string): Date => {
  const dateObj = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  return new Date(dateObj.toLocaleString("en-US", { timeZone: AEST_TIMEZONE }));
};

/**
 * Check if a date is today in AEST
 */
export const isToday = (date: Date | string): boolean => {
  const today = getTodayAEST();
  const checkDate = formatDateForInput(date);
  return today === checkDate;
};

/**
 * Create a Date object for a specific date in AEST timezone
 */
export const createAESTDate = (dateString: string): Date => {
  // Parse YYYY-MM-DD and create date at midnight AEST
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date();
  date.setFullYear(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  return date;
};

/**
 * Get relative time string (e.g., "2 hours ago", "yesterday")
 */
export const getRelativeTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = getCurrentAESTDate();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return formatDisplayDate(dateObj);
};